import { getFirebaseAuth } from '../app';

interface StripeApiResponse {
  id?: string;
  url?: string;
  error?: string;
}

async function callStripeApi(
  apiUrl: string,
  callName: string,
  variables: Record<string, string>,
): Promise<StripeApiResponse> {
  const auth = getFirebaseAuth();
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('User must be authenticated to call Stripe API');
  }

  const token = await currentUser.getIdToken();

  // Both sandbox and production Cloud Run read req.body.data — always wrap.
  const body = { data: { callName, variables } };

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error(`Stripe API error ${String(response.status)}:`, errorText);
    throw new Error(`Stripe API error: ${String(response.status)} — ${errorText}`);
  }

  const result = (await response.json()) as StripeApiResponse;

  // makeApiRequest in the backend swallows Stripe errors as HTTP 200 with body.error
  if (result.error) {
    console.error(`Stripe API returned error:`, result.error);
    throw new Error(`Stripe error: ${result.error}`);
  }

  return result;
}

export async function createCustomer(apiUrl: string, name: string, email: string): Promise<string> {
  const result = await callStripeApi(apiUrl, 'CreateCustomerCall', { name, email });
  if (!result.id) {
    throw new Error('Failed to create Stripe customer');
  }
  return result.id;
}

export async function createCheckoutSession(
  apiUrl: string,
  successUrl: string,
  cancelUrl: string,
  priceId: string,
  customerId: string,
  userId?: string,
): Promise<string> {
  const variables: Record<string, string> = {
    successUrl,
    cancelUrl,
    price: priceId,
    customer: customerId,
  };
  if (userId) {
    variables['userId'] = userId;
  }
  const result = await callStripeApi(apiUrl, 'CreateCheckoutSessionCall', variables);
  if (!result.url) {
    throw new Error('Failed to create checkout session');
  }
  return result.url;
}

/**
 * Create a Stripe Billing Customer Portal session and return its hosted URL.
 * The caller redirects the user to this URL so they can update their payment
 * method / billing info and view invoices (CAB-545). The portal is scoped in
 * the Stripe Dashboard to payment + invoices only.
 *
 * The Stripe customer is resolved SERVER-SIDE from the authenticated uid — the
 * client does not (and must not) pass a customer id (that would be an IDOR).
 */
export async function createBillingPortalSession(
  apiUrl: string,
  returnUrl: string,
): Promise<string> {
  const result = await callStripeApi(apiUrl, 'CreateBillingPortalSessionCall', {
    returnUrl,
  });
  if (!result.url) {
    throw new Error('Failed to create billing portal session');
  }
  return result.url;
}

export async function cancelSubscription(
  apiUrl: string,
  stripeSubscriptionId: string,
): Promise<void> {
  await callStripeApi(apiUrl, 'CancelSubscriptionAtPeriodEndCall', { stripeSubscriptionId });
}

export async function resumeSubscription(
  apiUrl: string,
  stripeSubscriptionId: string,
): Promise<void> {
  await callStripeApi(apiUrl, 'ResumeSubscriptionCall', { stripeSubscriptionId });
}

export async function updateSubscription(
  apiUrl: string,
  stripeSubscriptionId: string,
  stripeSubscriptionItemId: string,
  newPriceId: string,
  isDowngrade: boolean,
): Promise<void> {
  await callStripeApi(apiUrl, 'UpdateSubscriptionCall', {
    stripeSubscriptionId,
    stripeSubscriptionItemId,
    newPriceId,
    isDowngrade: isDowngrade ? 'true' : 'false',
  });
}
